import * as React from "react"
import { Navigate, useLocation } from "react-router-dom"

import { ApiError, getIdentity, logout as logoutRequest, onSessionLost } from "@/lib/api"
import type { Identity } from "@/lib/api"

/**
 * Кто вошёл — одно состояние на всю консоль (AMEND-8 §3 шаг 7).
 *
 * Хранить тут нечего, кроме личности: токен лежит в куке `httpOnly`, и код
 * страницы его не видит. Отсюда главное следствие — **на старте мы не знаем,
 * вошли мы или нет**, и обязаны спросить `/api/auth/me`. Поэтому у состояния
 * три положения, а не два: «спрашиваем», «никто», «вот этот человек». Слить
 * первое со вторым нельзя: тогда каждая перезагрузка страницы на долю секунды
 * выкидывала бы вошедшего на экран входа, а обратно возвращала бы уже с
 * потерянным адресом.
 *
 * Сюда же приходит 401, прилетевший **посреди работы**: сессия истекла или
 * админ выключил человека, пока тот смотрел в таблицу. Крюк `onSessionLost` из
 * клиента API гасит личность, и `RequireAuth` уводит на вход — вместо пустого
 * экрана с «нужен вход» где-нибудь в углу таблицы.
 */
interface AuthValue {
  /** `null` — не вошли. Пока `checking`, это «ещё не знаем», а не «никто». */
  identity: Identity | null
  /** Идёт первый вопрос `/auth/me`. */
  checking: boolean
  /**
   * Сессия оборвалась на живом человеке (а не «мы просто не входили»).
   * Экран входа объясняет этим, почему человек оказался на нём, — иначе
   * внезапный переход читается как поломка.
   */
  expired: boolean
  /** Вход состоялся: личность приехала из `/auth/verify`. */
  signedIn: (identity: Identity) => void
  /**
   * Аккаунт переименован его админом (шаг 8): поправить имя в личности.
   *
   * Отдельным действием, а не перезапросом `/auth/me`: имя приезжает из ответа
   * ручки переименования — то есть из базы, а не из введённого поля, — и второй
   * запрос за той же строкой ничего бы не уточнил. Личность при этом обязана
   * обновиться сразу: имя аккаунта стоит в шапке и отвечает на вопрос «в чьих
   * данных я сейчас». Шапка, показывающая старое имя до перезагрузки страницы,
   * — это ровно тот вопрос без ответа.
   *
   * Не вошли — ничего не делаем: переименовывать было некому.
   */
  accountRenamed: (name: string) => void
  /** Выход: гасим сессию в базе, потом забываем личность. */
  signOut: () => Promise<void>
}

const AuthContext = React.createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = React.useState<Identity | null>(null)
  const [checking, setChecking] = React.useState(true)
  const [expired, setExpired] = React.useState(false)

  // Крюку нужно знать, была ли личность в момент 401, но сам он должен
  // оставаться стабильным — иначе подписка пересоздавалась бы на каждый вход.
  const signedInRef = React.useRef(false)
  signedInRef.current = identity !== null

  React.useEffect(() => {
    const controller = new AbortController()
    getIdentity(controller.signal)
      .then((who) => setIdentity(who))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        // 401 — штатный ответ «никто не входил». Всё остальное (API лежит,
        // сеть отвалилась) тоже оставляет нас на экране входа: показывать
        // консоль, не зная, кто перед нами, нельзя.
        if (!(cause instanceof ApiError)) {
          console.error("session check failed", cause)
        }
        setIdentity(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setChecking(false)
      })
    return () => controller.abort()
  }, [])

  React.useEffect(
    () =>
      onSessionLost(() => {
        setExpired(signedInRef.current)
        setIdentity(null)
      }),
    [],
  )

  const value = React.useMemo<AuthValue>(
    () => ({
      identity,
      checking,
      expired,
      signedIn: (who: Identity) => {
        setExpired(false)
        setIdentity(who)
      },
      accountRenamed: (name: string) => {
        setIdentity((who) => (who ? { ...who, account_name: name } : who))
      },
      signOut: async () => {
        try {
          await logoutRequest()
        } finally {
          // Личность забываем в любом случае. Ручка выхода не требует живой
          // сессии и почти не отказывает, но если сеть отвалилась ровно на
          // выходе — остаться «вошедшим» страшнее, чем оставить строку в базе:
          // человек уже отвернулся от экрана.
          setExpired(false)
          setIdentity(null)
        }
      },
    }),
    [identity, checking, expired],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = React.useContext(AuthContext)
  if (!value) throw new Error("useAuth() вне <AuthProvider>")
  return value
}

/**
 * Заслон маршрута: без сессии — на `/login`, а не пустая страница.
 *
 * Адрес, куда человек шёл, уезжает в состояние перехода и возвращается после
 * входа. Без этого ссылка на карточку правила, открытая из чата, после входа
 * приводила бы на дашборд, и её пришлось бы искать заново.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { identity, checking } = useAuth()
  const location = useLocation()

  if (checking) return <SessionCheck />
  if (!identity) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  return <>{children}</>
}

/**
 * Полсекунды между «страница открылась» и ответом `/auth/me`.
 *
 * Пустое полотно тут читалось бы как сломанная сборка, а скелет таблицы — как
 * «данные уже грузятся», хотя мы ещё не знаем, покажем ли их вообще.
 */
function SessionCheck() {
  return (
    <div className="flex h-full items-center justify-center bg-canvas">
      <div className="flex items-center gap-2 text-fg-muted">
        <span className="h-3 w-3 animate-pulse rounded-sm bg-accent" />
        <span className="stencil-label">Checking session</span>
      </div>
    </div>
  )
}
